<template>
  <el-dialog
    :visible.sync="visible"
    :title="title || 'อัพโหลดรูปภาพ'"
    :close-on-click-modal="false"
    width="500px"
    append-to-body
    custom-class="sc-image-picker-modal"
    @dragover.native.prevent
    @drop.native.prevent
  >
    <div
      :class="{ 'drag-over': isDragOver }"
      class="upload-container"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div
        v-if="!previewUrl"
        class="upload-placeholder"
        @click="triggerFileInput"
      >
        <img :src="'/static/icons/button-add-cover.svg'" />
        <div>เพิ่มรูปภาพ</div>
        <div class="drag-hint">(คลิก หรือลากไฟล์ลงในกรอบนี้)</div>
        <div class="upload-hint">
          {{ uploadHintText }}
        </div>
      </div>

      <div v-else class="preview-area">
        <img :src="previewUrl" alt="Preview" class="preview-img" draggable="false" />
        <div class="preview-actions">
          <el-button
            size="small"
            type="danger"
            icon="el-icon-delete"
            circle
            @click="removeImage"
          ></el-button>
        </div>
      </div>

      <input
        ref="uploadInput"
        type="file"
        accept="image/png,image/jpeg"
        class="hidden-input"
        @change="handleFileChange"
      />
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">ยกเลิก</el-button>
      <el-button
        :disabled="!selectedFile"
        type="primary"
        @click="handleConfirm"
      >
        ยืนยัน
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    isShow: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    dimension: {
      type: Object,
      default: () => ({})
    },
    maxFileSizeMB: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      visible: false,
      selectedFile: null,
      previewUrl: null,
      isConfirmed: false,
      isDragOver: false,
    };
  },
  computed: {
    uploadHintText() {
      let hint = "* ";

      const recommendWidth = this.dimension?.recommendWidth;
      const recommendHeight = this.dimension?.recommendHeight;

      if (recommendWidth && recommendHeight) {
        hint += `ขนาดแนะนำ ${recommendWidth}x${recommendHeight} พิกเซล`;
      } else if (recommendWidth) {
        hint += `ความกว้างที่แนะนำ ${recommendWidth} พิกเซล`;
      } else if (recommendHeight) {
        hint += `ความสูงที่แนะนำ ${recommendHeight} พิกเซล`;
      }

      hint += `\n(และขนาดไฟล์สูงสุด ${this.maxFileSizeMB}MB)`;

      return hint;
    },
  },
  watch: {
    isShow(val) {
      if (val) {
        this.isConfirmed = false;
      }
      this.visible = val;
      if (!val) {
        this.resetData();
      }
    },
    visible(val) {
      if (!val) {
        this.$emit("update:isShow", false);
        this.$emit("close");
        if (!this.isConfirmed) {
          this.$emit("cancel");
        }
      }
    },
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
  methods: {
    resetData() {
      this.selectedFile = null;

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = null;

      this.isDragOver = false;
      if (this.$refs.uploadInput) {
        this.$refs.uploadInput.value = "";
      }
    },
    triggerFileInput() {
      if (this.$refs.uploadInput) {
        this.$refs.uploadInput.click();
      }
    },
    handleFileChange(event) {
      const { target } = event;
      if (target.files && target.files[0]) {
        this.processFile(target.files[0]);
      }
    },
    processFile(file) {
      const fileSizeMB = file.size / 1024 / 1024;

      const allowedTypes = ["image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        this.$message.error("กรุณาเลือกไฟล์ jpg/jpeg และ png เท่านั้น");
        return;
      }

      if (fileSizeMB > this.maxFileSizeMB) {
        this.$message.error(
          `${this.$t("feature.upload.exceed_max_file_size")} ${this.maxFileSizeMB} MB`
        );

        if (this.$refs.uploadInput) {
          this.$refs.uploadInput.value = "";
        }
        return;
      }

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    handleDragOver() {
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },
    handleDrop(event) {
      this.isDragOver = false;

      const { files } = event.dataTransfer;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    removeImage() {
      this.resetData();
    },
    handleClose() {
      this.visible = false;
    },
    handleConfirm() {
      this.isConfirmed = true;
      this.$emit("confirm", this.selectedFile);
      this.visible = false;
    },
  },
};
</script>

<style>
.sc-image-picker-modal {
  font-family: "Sukhumvit Tadmai", sans-serif !important;
}
.sc-image-picker-modal .el-dialog__title {
  font-family: "Sukhumvit Tadmai", sans-serif !important;
  font-weight: bold;
}
.sc-image-picker-modal .el-button {
  font-family: "Sukhumvit Tadmai", sans-serif !important;
}
</style>

<style scoped>
.upload-container {
  font-family: "Sukhumvit Tadmai", sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 20px;
  min-height: 200px;
  position: relative;
}

.upload-container.drag-over {
  border-color: #409eff;
  background-color: rgba(64, 158, 255, 0.06);
}

.upload-placeholder {
  text-align: center;
  cursor: pointer;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
}

.upload-placeholder i {
  font-size: 48px;
  color: #8c939d;
  margin-bottom: 10px;
}

.upload-hint {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

.drag-hint {
  font-size: 14px;
  color: #666;
  margin: 5px 0;
}

.hidden-input {
  display: none;
}

.preview-area {
  position: relative;
  width: 100%;
  text-align: center;
}

.preview-img {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.preview-actions {
  position: absolute;
  top: 10px;
  right: 10px;
}
</style>
